import { FormattedMessage, defineMessages, useIntl } from "react-intl";

import { evictCache } from "../../../lib/apollo/utils";
import { formatEntityNameLabel } from "../../../lib/formats/common";
import {
  CropStageChangeEvent,
  type CropStageChanges,
  type CropStageFields,
  type CropStageFragment,
  CropStageType,
} from "../../../lib/graphql/generated";
import {
  useCropStageChangeEventOptions,
  useCropStageCreate,
  useCropStageOptions,
  useCropStageTypeOptions,
} from "../../../lib/hooks/agro/cropStages";
import { useLocaleNameField } from "../../../lib/hooks/basic/locales";
import { useCurrentUser } from "../../../lib/hooks/basic/users";
import { DatePicker } from "../../form/DatePicker";
import { Form, Rules, shouldUpdate } from "../../form/Form";
import { LocalityCostCenterListFormItem } from "../../machines/LocalityCostCenterListFormItem";
import { ItemSidebar } from "../../shared/ItemSidebar";
import { ItemFieldFormConfig } from "../../shared/ListView/ItemForm";
import { NewForm } from "../../shared/ListView/NewForm";
import { LocaleEmoji } from "../../users/LocaleEmoji";
import { TranslationsInput } from "../../users/TranslationsInput";
import { CropSelect } from "../CropSelect";

interface CropStageSidebarProps {
  onSave: (result: CropStageFragment | null | undefined) => void;
}

interface NewCropStageFormProps {
  entityName: string;
  onClose: () => void;
  onSave?: CropStageSidebarProps["onSave"];
}

const messages = defineMessages({
  new: { id: "new.header", defaultMessage: "new" },
  cropStage: { id: "cropStages.entityName", defaultMessage: "Crop Stage" },
  home: { id: "home", defaultMessage: "home" },
  cropStages: {
    id: "cropStages",
    defaultMessage: "cropStages",
  },
  description: {
    id: "cropStages.description",
    defaultMessage: "description",
  },
  type: {
    id: "type",
    defaultMessage: "type",
  },
  prev: {
    id: "cropStages.prevStage",
    defaultMessage: "prevStage",
  },
  changeEvent: {
    id: "cropStages.changeEvent",
    defaultMessage: "changeEvent",
  },
  changeDate: { id: "cropStages.changeDate", defaultMessage: "Change Date" },
  pageTitle: { id: "cropStages.pageTitle", defaultMessage: "Crop Stagess" },
  created: { id: "created", defaultMessage: "created" },
  costCenter: { id: "costCenters.entityName", defaultMessage: "costCenter" },
});

export function useCropStageFormFields() {
  const intl = useIntl();
  const localeNameField = useLocaleNameField();
  const { currentTenant } = useCurrentUser();

  return [
    {
      label: formatEntityNameLabel(
        <FormattedMessage
          id="cropStages.entityName"
          defaultMessage="cropStage"
        />
      ),
      name: localeNameField,
      type: "string",
      prefix: <LocaleEmoji.Current />,
      rules: [Rules.required],
    },
    {
      label: intl.formatMessage(messages.type),
      name: "kind",
      type: "select",
      rules: [Rules.required],
      optionsHook: useCropStageTypeOptions,
    },
    {
      label: intl.formatMessage(messages.prev),
      name: "prevStageId",
      type: "select",
      showGroups: true,
      optionsHook: useCropStageOptions,
      optionsHookFilter: (c) => c.group,
    },
    {
      label: intl.formatMessage(messages.changeEvent),
      name: "changeEvent",
      type: "select",
      rules: [Rules.required],
      optionsHook: useCropStageChangeEventOptions,
    },
    {
      type: "custom",
      render: () => (
        <Form.Item
          key="changeDate"
          noStyle
          shouldUpdate={shouldUpdate("changeEvent")}
        >
          {({ getFieldValue }) => {
            if (getFieldValue("changeEvent") != CropStageChangeEvent.Date)
              return null;

            return (
              <Form.Item
                name="changeDate"
                label={intl.formatMessage(messages.changeDate)}
                rules={[Rules.required]}
              >
                <DatePicker asStringDate />
              </Form.Item>
            );
          }}
        </Form.Item>
      ),
    },
    {
      type: "custom",
      hidden: !currentTenant.features.costCenters,
      render: () => (
        <LocalityCostCenterListFormItem name="cropStageLocalitiesAttributes" />
      ),
    },
    {
      label: <FormattedMessage id="crops" />,
      name: "cropIds",
      formItemProps: {
        extra: (
          <FormattedMessage
            id="cropStages.cropsHelp"
            defaultMessage="cropsHelp"
          />
        ),
      },
      type: "custom",
      render: () => (
        <CropSelect
          mode="multiple"
          placeholder={<FormattedMessage id="crops.all" />}
          onlyUsed
        />
      ),
    },
  ] as ItemFieldFormConfig<CropStageFields | CropStageChanges>[];
}

export function NewCropStageForm({
  entityName,
  onSave,
  onClose,
}: NewCropStageFormProps) {
  const fields = useCropStageFormFields();

  return (
    <NewForm
      entityName={entityName}
      mutation={useCropStageCreate}
      onMutate={(result) => {
        if (onSave) onSave(result.cropStageCreate?.result);
        return result.cropStageCreate;
      }}
      mutationOptions={{ update: evictCache("cropStages") }}
      formValues={() =>
        ({
          kind: CropStageType.Production,
          name: "",
          changeEvent: CropStageChangeEvent.Manual,
          cropStageLocalitiesAttributes: [],
        }) as CropStageFields
      }
      onClose={onClose}
      fields={[
        ...fields,
        {
          type: "custom",
          render: TranslationsInput.renderName,
        },
      ]}
    />
  );
}

export function NewCropStageSidebar({ onSave }: CropStageSidebarProps) {
  const intl = useIntl();

  return (
    <ItemSidebar
      item={null}
      sidebarActions={{
        newCropStage: ({ closeSidebar }) => (
          <NewCropStageForm
            entityName={intl.formatMessage(messages.new, {
              entityName: intl.formatMessage(messages.cropStage),
            })}
            onSave={onSave}
            onClose={closeSidebar}
          />
        ),
      }}
    />
  );
}
